import { useState } from "react";

import { on } from "events";
import { groupBy, sortBy } from "lodash";
import { DateTime, Interval } from "luxon";
import { useTranslation } from "react-i18next";

import { ScheduledTime } from "Pages/OnlineOrderingSchedule/schema";

import { useVenue } from "../VenueContext";
import {
  useFetchOnlineOrderingSchedule,
  useFetchPickUpSettings,
} from "../queries";

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const getLuxonWeekDay = (day: string) => {
  return daysOfWeek.indexOf(day) + 1;
};
const isScheduledTimeNow = (scheduledTime: ScheduledTime) => {
  const now = DateTime.now();
  if (getLuxonWeekDay(scheduledTime.day) !== now.weekday) {
    return false;
  }
  return Interval.fromDateTimes(
    DateTime.fromISO(scheduledTime.startTime),
    DateTime.fromISO(scheduledTime.endTime),
  ).contains(now);
};

export const useOnlineOrderingAvailableNow = () => {
  const venue = useVenue();
  if (!venue.venueBrand.onlineOrderingWeeklyScheduleUuid) {
    return true;
  }
  const onlineOrderingSchedule = useFetchOnlineOrderingSchedule();

  const isOnlineOrderingEnabled =
    onlineOrderingSchedule.data?.scheduledTimes?.some(isScheduledTimeNow);
  return isOnlineOrderingEnabled;
};

export const useLastPickUpTime = () => {
  const pickUpSettingsQuery = useFetchPickUpSettings();
  const onlineOrderingSchedule = useFetchOnlineOrderingSchedule();
  if (!pickUpSettingsQuery.data || !onlineOrderingSchedule.data) {
    return undefined;
  }

  const currentScheduledTimes =
    onlineOrderingSchedule.data.scheduledTimes.filter(isScheduledTimeNow);
  const finalScheduledTime = sortBy(currentScheduledTimes, "endTime").pop();

  if (!finalScheduledTime) {
    return undefined;
  }
  const lastPickUpTimeIn = DateTime.fromISO(finalScheduledTime.endTime)
    .startOf("minute")
    .diff(DateTime.now().startOf("minute"), "minutes");
  return Math.min(
    lastPickUpTimeIn.minutes,
    pickUpSettingsQuery.data.noLaterThan,
  );
};
const OnlineOrderingDisabled = () => {
  const onlineOrderingSchedule = useFetchOnlineOrderingSchedule();
  const onlineOrderingAvailable = useOnlineOrderingAvailableNow();
  const [viewingOrderingHours, setViewingOrderingHours] = useState(false);
  const { t } = useTranslation();

  if (!!onlineOrderingAvailable) {
    return null;
  }
  return (
    onlineOrderingSchedule?.data && (
      <div className="bg-venue-highlight text-venue-secondary px-16 py-24 space-y-16 rounded-venue-brand">
        <div className=" font-semibold">
          {onlineOrderingSchedule.data.message ||
            t("onlineOrderingDisabled.onlineOrderingDisabledFallback")}
        </div>
        {viewingOrderingHours ? (
          <div className="text-venue-secondary space-y-12 text-sm">
            <div>
              {Object.entries(
                groupBy(onlineOrderingSchedule.data.scheduledTimes, "day"),
              ).map(([day, scheduledTimes]) => (
                <div className="flex gap-12">
                  <div>
                    {DateTime.fromObject({
                      weekday: getLuxonWeekDay(day),
                    }).toFormat("ccc")}
                  </div>
                  {scheduledTimes
                    .map((st) => `${st.startTime} - ${st.endTime}`)
                    .join(", ")}
                </div>
              ))}
            </div>
            <a
              className="cursor-pointer underline text-sm"
              onClick={() => setViewingOrderingHours(false)}
            >
              {t("onlineOrderingDisabled.hideOrderingHours")}
            </a>
          </div>
        ) : (
          <div>
            <a
              className="cursor-pointer text-sm font-normal text-venue-secondary underline"
              onClick={() => setViewingOrderingHours(true)}
            >
              {t("onlineOrderingDisabled.viewOrderingHours")}
            </a>
          </div>
        )}
      </div>
    )
  );
};

export default OnlineOrderingDisabled;
